import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import type { FetchCartResponse } from '@eliadmin/core/src/api/cart/cart-api';
import { setCartAddress, setDeliveryCartAddress } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { proformApiMutation, proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import type { ProformAddrItem } from '@eliadmin/core/src/api/proform/proform-types';
import { Field } from '@eliadmin/core/src/components/form/field';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
export const useProformAddrQueryOpts = () => {
  const me = useCustomerStore(s => s.me);

  return queryOptions({
    queryKey: ['proform-addr', me?.customer.email],
    queryFn: () => {
      return proformApiQuery('GET_USER_ADDRESS', { session_e_mail: me!.customer.email });
      // return proformApiQuery('GET_USER_ADDRESS', { session_e_mail: ' });
    },
    enabled: !!me,
  });
};

export function ProformAddrPicker() {
  const addrQuery = useQuery(useProformAddrQueryOpts());
  const form = useFormContext<CreateOrderFormValues>();
  const cartQuery = useCart();
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();
  const { queryKey: cartQueryKey } = useCartQueryOpts();
  const authToken = useCustomerStore(s => s.me?.authToken);
  const hasDeliveryAddress = cartQuery.data.delivery_address !== null;
  const [isDeliveryAddressOpen, setIsDeliveryAddressOpen] = useState(hasDeliveryAddress);

  const clearDeliveryAddressForm = () => {
    form.setValue('different_delivery_address', false, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue('address_delivery', undefined, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue('address2', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    form.setValue('street_number2', '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue('city2', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    form.setValue('postcode2', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    form.setValue(
      'country2',
      { id: '' },
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );
  };

  const selectAddrMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: async (poradi: string) => {
      return setCartAddress({ locale, poradi, authToken });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.SHIPMENTS] });
    },
    onMutate: vars => {
      const cart = queryClient.getQueryData<FetchCartResponse['data']>(cartQueryKey);
      if (!cart) return;
      // update optimistic UI
      cart.address = Number(vars) || null;
      queryClient.setQueryData<FetchCartResponse['data']>(cartQueryKey, cart);
    },
    onSuccess: data => {
      const cart = queryClient.getQueryData<FetchCartResponse['data']>(cartQueryKey);
      if (!cart) return;
      cart.address = Number(data.data.address) || null;
      queryClient.setQueryData<FetchCartResponse['data']>(cartQueryKey, cart);
    },
  });

  const phone = form.watch('phone');

  const selectDeliverAddrMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: async ({ poradi, item }: { poradi: string; item: ProformAddrItem }) => {
      if (item && !phone) {
        form.setValue('phone', item.mobil_preset + item.mobil);
      }
      return setDeliveryCartAddress({ locale, poradi, authToken });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.SHIPMENTS] });
    },
    onMutate: vars => {
      const cart = queryClient.getQueryData<FetchCartResponse['data']>(cartQueryKey);
      if (!cart) return;
      // update optimistic UI
      cart.delivery_address = Number(vars.poradi) || null;
      queryClient.setQueryData<FetchCartResponse['data']>(cartQueryKey, cart);
    },
    onSuccess: data => {
      const cart = queryClient.getQueryData<FetchCartResponse['data']>(cartQueryKey);
      if (!cart) return;
      cart.delivery_address =
        data.data.delivery_address === null ? null : Number(data.data.delivery_address) || null;
      setIsDeliveryAddressOpen(cart.delivery_address !== null);
      queryClient.setQueryData<FetchCartResponse['data']>(cartQueryKey, cart);
    },
  });

  const { t } = useTranslations();

  useEffect(() => {
    setIsDeliveryAddressOpen(hasDeliveryAddress);
  }, [hasDeliveryAddress]);

  const invItems = useMemo(() => {
    const invItems = addrQuery.data?.root.inv_address_items?.inv_address_item;

    if (!invItems) {
      return [];
    }

    let result: ProformAddrItem[] = [];

    if (Array.isArray(invItems)) {
      result = [...invItems].sort((a, b) => a.poradi.localeCompare(b.poradi));
    } else {
      result = [invItems];
    }

    return result;
  }, [addrQuery.data?.root.inv_address_items?.inv_address_item]);

  const delItems = useMemo(() => {
    const delItems = addrQuery.data?.root.del_address_items?.del_address_item;

    if (!delItems) {
      return [];
    }

    let result: ProformAddrItem[] = [];
    if (Array.isArray(delItems)) {
      result = [...delItems].sort((a, b) => a.poradi.localeCompare(b.poradi));
    } else {
      result = [delItems];
    }

    return result;
  }, [addrQuery.data?.root.del_address_items?.del_address_item]);

  useEffect(() => {
    const hasSelectedBillingAddress =
      cartQuery.data.address !== null && cartQuery.data.address !== undefined;

    if (hasSelectedBillingAddress || selectAddrMutation.isPending) {
      return;
    }

    const firstInvoiceAddress = invItems[0];
    if (!firstInvoiceAddress?.poradi) {
      return;
    }

    selectAddrMutation.mutate(firstInvoiceAddress.poradi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartQuery.data.address, invItems]);

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-row items-center justify-between gap-4">
          <h4 className="text-xl font-bold">{t('text45')}</h4>
          <CreateAddressDialog type="inv">
            <DialogTrigger asChild>
              <Button>{t('text1114')}</Button>
            </DialogTrigger>
          </CreateAddressDialog>
        </div>
        <div className="flex flex-row flex-wrap gap-6">
          {invItems.map(item => (
            <div key={item.poradi}>
              <AddrItem
                item={item}
                isSelected={String(item.poradi) === String(cartQuery.data.address)}
                onClick={() => {
                  // form.setValue('inv_addr_poradi', item.poradi);
                  selectAddrMutation.mutate(item.poradi);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <Collapsible open={isDeliveryAddressOpen} className="flex w-full flex-col gap-4">
        <CollapsibleTrigger asChild>
          {/* <Field
            control={form.control}
            name="different_delivery_address"
            type="checkbox"
            label={t('text365')}
          /> */}
          <div className="flex flex-row items-center gap-4">
            <Checkbox
              checked={isDeliveryAddressOpen}
              onCheckedChange={checked => {
                const shouldOpen = checked === true;
                setIsDeliveryAddressOpen(shouldOpen);
                if (!shouldOpen) {
                  clearDeliveryAddressForm();
                }
                selectDeliverAddrMutation.mutate({
                  poradi: shouldOpen ? String(delItems[0]?.poradi ?? 0) : '',
                  item: delItems[0],
                });
              }}
            />
            <Label>{t('text365')}</Label>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className="m-0 flex w-full flex-col gap-4 p-0 pb-8">
          <div className="flex w-full flex-row items-center justify-between gap-4">
            <h4 className="text-xl font-bold">{t('text32')}</h4>
            <CreateAddressDialog type="del">
              <DialogTrigger asChild>
                <Button>{t('text1114')}</Button>
              </DialogTrigger>
            </CreateAddressDialog>
          </div>
          <div className="flex flex-row flex-wrap gap-8">
            {delItems.map(item => (
              <AddrItem
                key={item.poradi}
                item={item}
                isSelected={String(item.poradi) === String(cartQuery.data.delivery_address)}
                onClick={() => {
                  // form.setValue('del_addr_poradi', item.poradi);
                  setIsDeliveryAddressOpen(true);
                  selectDeliverAddrMutation.mutate({ poradi: item.poradi, item });
                }}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

type AddrItemProps = {
  item: ProformAddrItem;
  isSelected: boolean;
  onClick: () => void;
};

export function AddrItem({ item, isSelected, onClick }: AddrItemProps) {
  const mobil = `${item.mobil_preset} ${item.mobil}`;

  const getMobil = () => {
    if (mobil.includes('+')) {
      return mobil;
    } else {
      return `+${mobil}`;
    }
  };

  return (
    <Card
      variant={'outlined'}
      className={cn(
        'min-w-[300px] shadow-none transition-all duration-200',
        isSelected && 'border-green-600 bg-green-600/10 shadow-lg shadow-green-600/10',
        !isSelected && 'cursor-pointer hover:shadow-lg',
      )}
      onClick={onClick}
    >
      <CardContent className="flex flex-col gap-4 p-4">
        <h5 className="text-lg font-semibold">{item.nazev}</h5>
        <div className="flex flex-col gap-1">
          <p className="text-sm text-muted-foreground">
            {item.ulice} {item.cislo_popisne}, {item.mesto} {item.psc}
          </p>
          <p className="text-sm text-muted-foreground">{item.stat_txt || item.stat}</p>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-sm text-muted-foreground">
            {item.kontaktni_osoba} {item.kontaktni_osoba_p}
          </p>
          <p className="text-sm text-muted-foreground">{item.e_mail}</p>
          <p className="text-sm text-muted-foreground">{getMobil()}</p>
        </div>
      </CardContent>
    </Card>
  );
}

type CreateAddressDialogProps = {
  type: 'del' | 'inv';
};

const createAddressSchema = z.object({
  name: z.string().min(1),
  surname: z.string().min(1),
  ic: z.string().optional().default(''),
  dic: z.string().optional().default(''),
  vat: z.string().optional().default(''),
  street: z.string().min(1),
  street_number: z.string().min(1),
  city: z.string().min(1),
  zip: z.string().min(1),
  country: z.object({
    id: z.string().min(1),
  }),
  // region: z.string().min(1).optional(),
  phone: z.string().min(1),
});
type CreateAddressValues = z.infer<typeof createAddressSchema>;

export function CreateAddressDialog(props: PropsWithChildren<CreateAddressDialogProps>) {
  const [isOpen, setIsOpen] = useState(false);

  const locale = useLocaleStore(s => s.currentLocale);
  const form = useForm<CreateAddressValues>({
    defaultValues: {
      name: '',
      surname: '',
      ic: '',
      dic: '',
      vat: '',
      street: '',
      street_number: '',
      city: '',
      zip: '',
      country: { id: '' },
      phone: '',
    },
    resolver: zodResolver(createAddressSchema),
  });

  const email = useCustomerStore(s => s.me?.customer.email);
  const { t } = useTranslations();

  const queryClient = useQueryClient();
  const saveUserAddressMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: async (values: CreateAddressValues) => {
      const phonePreset = values.phone.startsWith('+') ? values.phone.slice(0, 4) : undefined;
      const phone = values.phone.startsWith('+') ? values.phone.slice(4) : values.phone;

      return proformApiMutation('SAVE_USER_ADDRESS', {
        session_e_mail: email!,
        poradi: 0,
        typ_adresy: props.type === 'inv' ? 20 : 10,
        adresa_cislo_popisne: values.street_number,
        adresa_firma_dic: values.dic,
        adresa_firma_ic: values.ic,
        adresa_firma_ic_dph: values.vat,
        adresa_jmeno: values.name,
        adresa_mesto: values.city,
        adresa_prijmeni: values.surname,
        adresa_psc: values.zip,
        adresa_stat: values.country.id,
        adresa_ulice: values.street,
        adresa_telefon_preset: phonePreset,
        adresa_telefon: phone,
        adresa_mobil_preset: phonePreset,
        adresa_mobil: phone,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['proform-addr'] });
    },
  });

  const { data: countries = { data: [] } } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await saveUserAddressMutation.mutateAsync(values);
      form.reset();
      setIsOpen(false);
    } catch (e) {
      console.error(e);
    }
  });

  const countryId = form.watch('country.id');

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {props.children}
      <Form {...form}>
        <DialogContent className="flex max-h-[100vh] w-[700px] max-w-full flex-col gap-4 overflow-y-auto sm:max-h-[700px] sm:max-w-[700px]">
          <DialogTitle hidden>{t('text1114')}</DialogTitle>
          <form className="grid grid-cols-12 gap-4" onSubmit={handleSubmit}>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="name" label={t('text33')} required />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="surname" label={t('text34')} required />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="phone" label={t('text36')} type="tel" />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field
                control={form.control}
                name="country.id"
                label={t('text41')}
                type="select"
                options={countries.data.map(c => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="ic" label={t('text42')} />
            </div>
            {countryId === 'SK' && (
              <div className={cn('col-span-12 sm:col-span-6')}>
                <Field control={form.control} name="dic" label={t('text43')} />
              </div>
            )}
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="vat" label={t('text44')} />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="street" label={t('text38')} required />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="street_number" label={t('text438')} required />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="city" label={t('text39')} required />
            </div>
            <div className={cn('col-span-12 sm:col-span-6')}>
              <Field control={form.control} name="zip" label={t('text40')} required />
            </div>
            <div className="col-span-12 w-full">
              <div className="flex w-full flex-row justify-end gap-4 ">
                <Button
                  variant="outline"
                  onClick={() => {
                    form.reset();
                    setIsOpen(false);
                  }}
                  className="flex-1 sm:flex-none"
                >
                  {t('text272')}
                </Button>
                <Button
                  onClick={() => {}}
                  className="flex-1 sm:flex-none"
                  type="submit"
                  isLoading={form.formState.isSubmitting}
                >
                  {t('text201')}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
}
